<div class="tutorial-content">
  
  <!-- Introduction & Warning -->
  <div class="intro-section">
    <h1>Audience Anarchy Writing Tutorial</h1>
    
    <mat-card class="toc-card">
      <mat-card-header>
        <mat-card-title>Quick Start Guide</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <p>Creating a script is as simple as filling in boxes on the <a [routerLink]="['/scripts/edit/new']">Script Editor</a> page:</p>
        <ol>
            <li><strong>Basics:</strong> Choose a Title and Backdrop.</li>
            <li><strong>Characters:</strong> Define up to 6 characters (e.g., "DOCTOR").</li>
            <li><strong>Suggestions:</strong> Define mad-libs style prompts for the audience (e.g., "TYPE OF FOOD").</li>
            <li><strong>Write:</strong> Write the dialogue using the tags you defined (e.g., "DOCTOR: Hello!").</li>
            <li><strong>Verify:</strong> Click "Check Formatting" to catch errors.</li>
            <li><strong>Submit:</strong> Click "Submit" to send it for approval.</li>
        </ol>
      </mat-card-content>
        <mat-card-actions align="end">
             <a mat-button color="primary" [routerLink]="['/scripts/submit/example']">Open Example Script</a>
        </mat-card-actions>
    </mat-card>
  </div>

  <!-- Table of Contents -->
  <mat-card class="toc-card">
    <mat-card-content>
        <h3>Table of Contents</h3>
        <ul>
            <li><a href="javascript:void(0)" (click)="section1.scrollIntoView({ behavior: 'smooth' })" class="discord-link">1. How Audience Anarchy Works</a></li>
            <li><a href="javascript:void(0)" (click)="section2.scrollIntoView({ behavior: 'smooth' })" class="discord-link">2. Script Editor</a></li>
            <li><a href="javascript:void(0)" (click)="section3.scrollIntoView({ behavior: 'smooth' })" class="discord-link">3. Defining Characters</a></li>
            <li><a href="javascript:void(0)" (click)="section4.scrollIntoView({ behavior: 'smooth' })" class="discord-link">4. Audience Suggestions</a></li>
            <li><a href="javascript:void(0)" (click)="section5.scrollIntoView({ behavior: 'smooth' })" class="discord-link">5. Writing Script Text</a></li>
            <li><a href="javascript:void(0)" (click)="section6.scrollIntoView({ behavior: 'smooth' })" class="discord-link">6. Formatting Rules</a></li>
            <li><a href="javascript:void(0)" (click)="section7.scrollIntoView({ behavior: 'smooth' })" class="discord-link">7. Content Guidelines</a></li>
            <li><a href="javascript:void(0)" (click)="section8.scrollIntoView({ behavior: 'smooth' })" class="discord-link">8. About Plagiarism</a></li>
            <li><a href="javascript:void(0)" (click)="section9.scrollIntoView({ behavior: 'smooth' })" class="discord-link">9. Full Example Script</a></li>
        </ul>
    </mat-card-content>
  </mat-card>

  <!-- Detailed Sections -->
  
  <!-- 1. How it works -->
  <div #section1 class="section-anchor">
      <h2>1. How Audience Anarchy Works</h2>
      <p>Audience Anarchy is a game world where players act out pre-written scripts. Scripts are "Mad-Libs" style, meaning the audience fills in the blanks before the show starts.</p>
      <p>On jar.games, you can write and submit your own scripts to be features in the game! Please note that all scripts get manually reviewed, so it may take a month or two to get approved.</p>
  </div>

  <div #section2 class="section-anchor">
    <h2>2. Script Editor</h2>
    <p>
        Creating a script is as simple as filling in the boxes!<br>
    </p>
    <ul>
        <li>
            First, chose a title, genre, and backdrop for your script
        </li>
        <li>
            Then, define the characters of your script, as well as the audience suggestions, which are the parts of the script the audience can fill in
        </li>
        <li>
            Finally, write the script using the characters and suggestions you defined.
        </li>
    </ul>
    <p>
        You can save your progress at any time by clicking "I will continue editing later"<br>
        Once you are happy with your script, click "I'm done editing" to submit it for review!<br>
        After being submitted, you can still edit your script to make changes and improvements. Edits will only show up in-game after being approved by Jar.
    </p>

  </div>

  <!-- 3. Characters -->
  <div #section3 class="section-anchor">
      <h2>3. Defining Characters</h2>
      <p>When defining a character, you will be prompted for 3 things:</p>
      <ul>
          <li><b>Character Tag</b>: This is how you will referr to the character in the script</li>
          <li><b>Character Name</b>: The name of the character as seen by the actors</li>
          <li><b>Starting location</b>: Where this character will be positioned at the start of the script</li>
      </ul>
      <p>
        The character tag should be a short but identifiable label (i.e. "DOC"). It will not be seen by the actors or audience.<br>
        The character name should be the a more descriptive name (i.e. "The evil Doctor"). It will be seen by the actors, and should allow the actor to know what role they are signing up for.
      </p>
      
      <div class="comparison-container">
          <div class="example-box good-example">
              <h4><mat-icon>check_circle</mat-icon> Good Character Labels</h4>
              <p>Short, descriptive roles that let actors know what they are signing up for.</p>
              <ul>
                  <li>"The Confused Doctor"</li>
                  <li>"Angry Pizza Guy"</li>
                  <li>"Mom"</li>
              </ul>
          </div>
          <div class="example-box bad-example">
              <h4><mat-icon>cancel</mat-icon> Bad Character Labels</h4>
              <p>Too vague, too long, or using internal names.</p>
              <ul>
                  <li>"Char 1" (Too vague)</li>
                  <li>"Bob" (Unless Bob is a famous character, say "Bob the Builder")</li>
                  <li>"The guy who walks in and shoots everyone then leaves" (Too long)</li>
              </ul>
          </div>
      </div>
  </div>

  <!-- 4. Suggestions -->
  <div #section4 class="section-anchor">
      <h2>4. Audience Suggestions</h2>
      <p>
        These are the blanks the audience fills in.<br>
        Simmilar to the Characters, you define a tag and a prompt:
      </p>
      <ul>
          <li><b>Suggestion Tag</b>: This is how you will referr to the suggestion in the script</li>
          <li><b>Suggestion Prompt</b>: The prompt the audience will see</li>
          <li><b>Examples</b>: A comma separated list of examples to help the audience understand what you are looking for. Used for the "Suggest for me" button</li>
      </ul>

      <div class="comparison-container">
          <div class="example-box good-example">
              <h4><mat-icon>check_circle</mat-icon> Good Suggestion Prompts</h4>
              <p>Specific enough to get grammatical results, but open enough for creativity.</p>
              <ul>
                  <li>"A type of food"</li>
                  <li>"Something not worth fighting a war over"</li>
                  <li>"Something that takes physical effort to do"</li>
              </ul>
          </div>
          <div class="example-box bad-example">
              <h4><mat-icon>cancel</mat-icon> Bad Suggestion Prompts</h4>
              <p>Confusing instructions or asking for something too specific.</p>
              <ul>
                  <li>"Type something funny here"</li>
                  <li>"A word that rhymes with orange"</li>
              </ul>
          </div>
      </div>

      <p>
        Suggestions should be used to add fun variety to the script.<br>
        Some suggestions either do not add variety, or are not fun to use. Avoid these:
    </p>
    <div class="comparison-container">
          <div class="example-box bad-example">
              <h4><mat-icon>dangerous</mat-icon> Forbidden Suggestion Prompts</h4>
              <ul>
                  <li><b>Inappropriate</b>: "Something insulting", "A group of people you don't like"</li>
                  <li><b>Character names</b>: "A name for a child" - These do not add variety</li>
              </ul>
          </div>
      </div>
  </div>

  <!-- 5. Writing Script Text -->
  <div #section5 class="section-anchor">
      <h2>5. Writing Script Text</h2>
      <p>The "Script Text" is the actual screenplay. For every line of dialogue, you must start with the character's <strong>TAG</strong>, followed by a colon.</p>
      
      <div class="comparison-container">
          <div class="example-box good-example">
              <h4><mat-icon>check_circle</mat-icon> Good Dialogue</h4>
              <p>Clear formatting, includes actions in parentheses, and uses suggestion tags.</p>
              <div class="read-only-editor" style="padding: 8px;">
                  <span class="line"><span class="tag">DOCTOR:</span> Hello! I am ready to eat some <span class="suggestion-tag">FOOD</span>.</span>
                  <span class="line"><span class="tag">PATIENT:</span> (Scream in pain) My stomach hurts!</span>
                  <span class="line"><span class="tag">DOCTOR:</span> (Point at patient) That is because you ate <span class="suggestion-tag">POISON</span>!</span>
              </div>
          </div>
          <div class="example-box bad-example">
              <h4><mat-icon>cancel</mat-icon> Bad Dialogue</h4>
              <p>Missing tags, lowercase tags (if Defined as UPPERCASE), or simple text without a speaker.</p>
              <div class="read-only-editor" style="padding: 8px;">
                  <span class="line"><span class="comment"># Missing character tag:</span></span>
                  <span class="line">Hello everyone!</span>
                  <span class="line"><span class="comment"># Action without speech:</span></span>
                  <span class="line"><span class="tag">DOCTOR:</span> (Dies)</span>
              </div>
          </div>
      </div>
      
      <h3>Special Commands</h3>
      There are a few special commands you can use to add more than just character dialogue to your script.
      <ul>
          <li>
            <strong>Teleporting:</strong><br>
            <p>
                <code>Teleport TAG to LOCATION</code> (e.g., <code>Teleport DOCTOR to Backstage</code>)<br>
                This instantly moves a character to a location. It's often better to make the characters walk (e.g. <code>DOCTOR: I'll have to go now! (Exit stage left)</code>)
            </p>
        </li>
          <li>
            <strong>Sound Effects & Music:</strong><br>
            <p>
                Just write the sound name on a new line (e.g., <code>Gunshot</code>). Check the editor for the full list.<br>
                If you want to stop a sound, write <code>Sound Stops</code>.
            </p>
        </li>
          <li>
            <strong>Meanwhile:</strong><br>
            <p>
                Use <code>Meanwhile TAG: ...</code> to make a character act simultaneously with the previous line.<br>
                Example:
            </p>
            <div class="read-only-editor" style="padding: 8px;">
                <span class="line"><span class="tag">HOMEOWNER:</span> Oh man, I hope nobody is sneaking around my house right now!</span>
                <span class="line"><span class="tag">Meanwhile BURGLAR:</span> (Slowly sneak into the house)</span>
            </div>
        </li>
      </ul>
  </div>

  <!-- 6. Formatting Rules -->
  <div #section6 class="section-anchor">
    <h2>6. Formatting Rules & Troubleshooting</h2>
    <p>The "Check Formatting" button is your friend. If it turns <span style="color: var(--mat-sys-primary);">green</span>, you are good to go. If <span style="color: var(--mat-sys-error);">red</span>, check the error message.</p>

    <div class="comparison-container">
        <div class="example-box good-example">
            <h4><mat-icon>check_circle</mat-icon> Common Fixes</h4>
            <ul>
                <li><strong>Check Character Tags:</strong> Did you spell <code>DOCTOR</code> correctly every time?</li>
                <li><strong>Check Colons:</strong> Did you forget the <code>:</code> after the name?</li>
                <li><strong>Check Locations:</strong> Does every character have a "Starting Location" selected in the top menu?</li>
            </ul>
        </div>
        <div class="example-box bad-example">
            <h4><mat-icon>cancel</mat-icon> Common Mistakes</h4>
            <ul>
                 <li><strong>Using "Typewriter" Character:</strong> You don't need to add "TYPEWRITER" to your character list. It's a built-in feature.</li>
                 <li><strong>Recursive Suggestions:</strong> Don't put a Suggestion Tag inside the "Example" field of another Suggestion.</li>
            </ul>
        </div>
    </div>
  </div>

  <!-- 7. Guidelines -->
  <div #section7 class="section-anchor">
      <h2>7. Content Guidelines</h2>
      <p>We want Audience Anarchy to be fun for everyone. Please follow these rules:</p>
      <ul>
          <li><strong>PG-13 Only:</strong> No explicit content. Mild swearing is okay. Make sure to tag innuendos.</li>
          <li><strong>No Hate Speech:</strong> Zero tolerance for racism, sexism, homophobia, transphobia, etc.</li>
          <li><strong>Original Content:</strong> Don't just copy-paste entire movie scripts. You can take inspiration, but you cannot copy. See <a href="javascript:void(0)" (click)="section8.scrollIntoView({ behavior: 'smooth' })">8. About plagiarism</a> for more details</li>
          <li><strong>Keep it Simple:</strong> Scripts that are too long or require complex physical acting usually fail.</li>
          <li><strong>Avoid "Pretend":</strong> Don't write <code>(Pretend to die)</code>. Just write <code>(Die dramatically)</code>.</li>
      </ul>
  </div>

  <!-- 8. About Plagiarism -->
  <div #section8 class="section-anchor">
    <h2>8. About Plagiarism</h2>
    <p>A common reason for scripts to be rejected is plagiarism. While you are allowed to take inspiration from other works, there are some strict limits</p>
        <div class="comparison-container">
        <div class="example-box good-example">
            <h4><mat-icon>check_circle</mat-icon> What's allowed</h4>
            <ul>
                <li><strong>Copy the setting:</strong> If you want to write a script about an infomercial, you can take some inspiration on how infomercials are usually written, some common phrases used in them, and even some common character tropes</li>
            </ul>
        </div>
        <div class="example-box bad-example">
            <h4><mat-icon>cancel</mat-icon> What's not allowed</h4>
            <ul>
                 <li><strong>Copying dialogue:</strong> If you are simply transcribing dialogue from a show or movie, you are plagiarizing. Adding suggestions after the fact does not change this.</li>
                 <li><strong>Copying the plot:</strong> Even if you rephrase the exact wording, if your script has the same plot, characers, and scenes as another work, you are copying it too closely</li>
            </ul>
        </div>
    </div>
  </div>

  <!-- 9. Example Script -->
  <div #section9 class="section-anchor" style="margin-top: 40px;">
    <h2>9. Full Example Script</h2>
    <p>We have provided a full example script for you to look at. You can open it in a read-only version of the editor to see exactly how it looks.</p>
    
    <div style="display: flex; justify-content: center; padding: 20px;">
        <a mat-flat-button color="primary" [routerLink]="['/scripts/submit/example']">
            <mat-icon>open_in_new</mat-icon>
            Open Example Script
        </a>
    </div>
  </div>

  <div class="section-anchor">
    <h2>Still have questions?</h2>
    <p>
        If you need help writing your script, you can always ask for help in <a mat-button color="primary" target="_blank" href="https://discord.gg/YVwqYkGwq4">#script-writing</a> on the <a target="_blank" href="https://discord.gg/cult-of-jar">Discord server</a>!
    </p>
  </div>

  <div style="height: 100px;"></div>
</div>